import { Badge, Button, Dialog, Box, Flex, Text, Heading, ScrollArea, IconButton } from '@radix-ui/themes';
import React, { useState } from 'react';
import { MdOutlineVerifiedUser, MdMedication, MdOutlineTask } from 'react-icons/md';
import { CgArrowsExpandUpRight } from 'react-icons/cg';
import { RiDeleteBin5Fill } from 'react-icons/ri';
import { createPortal } from 'react-dom';
import axiosInstance from '../../config/axiosConfig';
import ConfirmationDialogue from './ConfirmationDialogue';
import { formatCalendarDate } from '../../utils/helpers';

const ViewMedication = ({ initialValues, onReminderUpdated, showToast, canManage = true }) => {
  const [isViewOpen, setViewOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null);

  // Determine if this is an Action type reminder
  const isAction = initialValues.reminder.type === 'Action' || initialValues.reminder.type === 2;

  const handleConfirmation = (type) => {
    if (!canManage) return;

    setViewOpen(false);
    setConfirmationType(type);
    setConfirmationOpen(true);
  };

  const confirmationHeadings = {
    Delete: 'Deletion',
    Activate: 'Activation',
    Deactivate: 'Deactivation',
  };

  const handleDelete = async () => {
    try {
      const response = await axiosInstance.delete(`/MedicActionReminder/delete/${initialValues.reminder.token}`);
      if (response.data.result) {
        if (onReminderUpdated) {
          await onReminderUpdated({ token: initialValues.reminder.token, deleted: true });
        }
        return true;
      } else {
        console.error('Delete failed:', response.data);
        if (showToast) showToast(response.data.errorResponse?.message || 'Failed to delete reminder', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
      if (showToast) showToast(error.response?.data?.errorResponse?.message || 'Error deleting reminder', 'error');
      return false;
    }
  };

  const handleUpdateStatus = async (isActive) => {
    try {
      const reminderData = {
        Token: initialValues.reminder.token,
        IsActive: isActive,
      };
      const response = await axiosInstance.put('/MedicActionReminder/update', reminderData);
      if (response.data.result) {
        if (onReminderUpdated) {
          await onReminderUpdated({ token: initialValues.reminder.token, isActive });
        }
        return true;
      } else {
        console.error('Update failed:', response.data);
        if (showToast) showToast(response.data.errorResponse?.message || 'Failed to update reminder status', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error updating reminder:', error);
      if (showToast) showToast(error.response?.data?.errorResponse?.message || 'Error updating reminder status', 'error');
      return false;
    }
  };

  const handleConfirmAction = async () => {
    let success = false;
    let actionMessage = '';
    if (confirmationType === 'Delete') {
      success = await handleDelete();
      actionMessage = 'Deleted';
    } else if (confirmationType === 'Activate') {
      success = await handleUpdateStatus(true);
      actionMessage = 'Activated';
    } else if (confirmationType === 'Deactivate') {
      success = await handleUpdateStatus(false);
      actionMessage = 'Deactivated';
    }

    if (success) {
      const message = `Reminder ${actionMessage} successfully`;
      if (showToast) showToast(message);
      setConfirmationOpen(false);
    } else {
      setConfirmationOpen(false);
      setViewOpen(true);
    }
  };

  // Format date in the desired format
  const formatDate = (dateString) => {
    return formatCalendarDate(dateString);
  };

  // Format time to 12-hour format with AM/PM
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    
    // Check if the time is already in 12-hour format
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    // Convert 24-hour format to 12-hour format
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12; // Convert 0 to 12
    
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Format the days of the week
  const formatDays = () => {
    const dayAbbreviations = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayProps = ['isSunday', 'isMonday', 'isTuesday', 'isWednesday', 'isThursday', 'isFriday', 'isSaturday'];
    
    const selectedDays = dayProps
      .map((prop, index) => initialValues.repetition[prop] ? dayAbbreviations[index] : null)
      .filter(day => day !== null);
    
    if (selectedDays.length === 7) {
      return <Badge color="blue">Every day</Badge>;
    } else if (selectedDays.length === 0) {
      return <Badge color="gray">No days selected</Badge>;
    } else if (JSON.stringify(selectedDays) === JSON.stringify(['Mon', 'Tue', 'Wed', 'Thu', 'Fri'])) {
      return <Badge color="green">Weekdays</Badge>;
    } else if (JSON.stringify(selectedDays) === JSON.stringify(['Sat', 'Sun'])) {
      return <Badge color="orange">Weekends</Badge>;
    }
    
    return (
      <Flex gap="1" wrap="wrap">
        {selectedDays.map((day, index) => (
          <Badge key={index} color="blue" variant="soft" size="1">
            {day}
          </Badge>
        ))}
      </Flex>
    );
  };

  const dialogContent = isViewOpen ? (
    <Dialog.Content maxWidth="500px">
      <Dialog.Title>
        <Flex align="center" gap="2">
          {isAction ? (
            <MdOutlineTask size={24} color="#0091ff" />
          ) : (
            <MdMedication size={24} color="#0091ff" />
          )}
          {isAction ? 'Task Details' : 'Medication Details'}
        </Flex>
      </Dialog.Title>
      
      <Box my="4" p="3" style={{ backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
        <Flex direction="column" gap="2">
          <Badge 
            size="2" 
            variant="surface" 
            color={initialValues.reminder.isActive ? 'green' : 'tomato'}
            style={{ alignSelf: 'flex-start', marginBottom: '8px' }}
          >
            <MdOutlineVerifiedUser size={16} />
            {initialValues.reminder.isActive ? 'Active' : 'Inactive'}
          </Badge>
          
          <Flex gap="4" wrap="wrap">
            <Flex direction="column" gap="1" style={{ minWidth: '120px' }}>
              <Text size="1" color="gray">SCHEDULE</Text>
              <Text size="3" weight="bold">{formatTime(initialValues.reminder.time)}</Text>
            </Flex>
            
            <Flex direction="column" gap="1" style={{ flex: 1 }}>
              <Text size="1" color="gray">DAYS</Text>
              {formatDays()}
            </Flex>
          </Flex>
          
          <Flex gap="4" wrap="wrap" mt="2">
            <Flex direction="column" gap="1" style={{ minWidth: '120px' }}>
              <Text size="1" color="gray">FROM</Text>
              <Text size="2">{formatDate(initialValues.reminder.from)}</Text>
            </Flex>
            
            <Flex direction="column" gap="1" style={{ flex: 1 }}>
              <Text size="1" color="gray">TO</Text>
              <Text size="2">{formatDate(initialValues.reminder.to)}</Text>
            </Flex>
          </Flex>
        </Flex>
      </Box>
      
      <Box>
        <Heading size="3" mb="2">
          {isAction ? 'Task List' : 'Medication List'}
        </Heading>
        
        <ScrollArea style={{ maxHeight: '200px' }}>
          {initialValues.details.map((detail, index) => (
            <Box 
              key={index} 
              p="3" 
              mb="2" 
              style={{ 
                backgroundColor: '#f5f8ff', 
                borderRadius: '6px',
                borderLeft: '4px solid #0091ff'
              }}
            >
              <Flex direction="column" gap="1">
                <Text weight="bold" size="3">{detail.medication}</Text>
                
                {!isAction && (
                  <Flex gap="3" mt="1">
                    {detail.dosage && (
                      <Flex direction="column" gap="1">
                        <Text size="1" color="gray">DOSAGE</Text>
                        <Text size="2">{detail.dosage}</Text>
                      </Flex>
                    )}
                    
                    {detail.root && (
                      <Flex direction="column" gap="1">
                        <Text size="1" color="gray">ROOT</Text>
                        <Text size="2">{detail.root}</Text>
                      </Flex>
                    )}
                  </Flex>
                )}
              </Flex>
            </Box>
          ))}
        </ScrollArea>
      </Box>
      
      <Flex gap="2" justify="end" mt="4">
        <Button
          color={initialValues.reminder.isActive ? 'crimson' : 'green'}
          onClick={() => handleConfirmation(initialValues.reminder.isActive ? 'Deactivate' : 'Activate')}
          disabled={!canManage}
        >
          {initialValues.reminder.isActive ? 'Deactivate' : 'Activate'}
        </Button>
        <Button onClick={() => handleConfirmation('Delete')} disabled={!canManage}>
          <RiDeleteBin5Fill /> Delete
        </Button>
      </Flex>
    </Dialog.Content>
  ) : null;

  return (
    <>
      <Dialog.Root open={isViewOpen} onOpenChange={setViewOpen}>
        <Dialog.Trigger>
         <IconButton variant="solid" onClick={() => setViewOpen(true)}>
            <CgArrowsExpandUpRight width="18" height="18" />
          </IconButton>
        </Dialog.Trigger>
        {isViewOpen && createPortal(dialogContent, document.body)}
      </Dialog.Root>
      {confirmationOpen && (
        <ConfirmationDialogue
          Name={initialValues.details[0]?.medication || 'Reminder'}
          Type={confirmationHeadings[confirmationType] || 'Confirm Action'}
          Action={confirmationType.toLowerCase()}
          onCancel={() => setConfirmationOpen(false)}
          onConfirm={handleConfirmAction}
        />
      )}
    </>
  );
};

export default ViewMedication;
